import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import ChatBubble from "@/components/support/ChatBubble";
import ChatLauncher from "@/components/support/DraggableChatLauncher";

import { MessageCircle, X, Send, Loader2, Minus } from "lucide-react";

export default function LiveChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [ticket, setTicket] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [agentTyping, setAgentTyping] = useState(false);
  const [unread, setUnread] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingRef = useRef<any>(null);

  // Load or create live chat ticket
  const initChat = useCallback(async () => {
    if (!user) return;
    const { data: existing } = await supabase
      .from("support_tickets")
      .select("*")
      .eq("user_id", user.id)
      .eq("is_live", true)
      .neq("status", "closed")
      .order("created_at", { ascending: false })
      .limit(1);

    if (existing && existing.length > 0) {
      setTicket(existing[0]);
      loadMessages(existing[0].id);
    }
  }, [user?.id]);

  const loadMessages = async (ticketId: string) => {
    const { data } = await supabase
      .from("support_messages")
      .select("*")
      .eq("ticket_id", ticketId)
      .order("created_at", { ascending: true });
    setMessages(data || []);
    setUnread(0);
  };

  useEffect(() => {
    if (open && !ticket) initChat();
  }, [open, ticket, initChat]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Realtime messages
  useEffect(() => {
    if (!ticket) return;
    const channel = supabase
      .channel(`user-live-chat-${ticket.id}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "support_messages",
        filter: `ticket_id=eq.${ticket.id}`,
      }, (payload: any) => {
        setMessages((prev) => [...prev, payload.new]);
        if (payload.new.sender_type === "admin" && (!open || minimized)) {
          setUnread((u) => u + 1);
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [ticket?.id, open, minimized]);

  // Realtime typing
  useEffect(() => {
    if (!ticket) return;
    const channel = supabase
      .channel(`user-live-typing-${ticket.id}`)
      .on("postgres_changes", {
        event: "UPDATE",
        schema: "public",
        table: "support_tickets",
        filter: `id=eq.${ticket.id}`,
      }, (payload: any) => {
        setAgentTyping(!!payload.new.agent_typing);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [ticket?.id]);

  if (!user) return null;

  const startChat = async () => {
    const { data, error } = await supabase.from("support_tickets").insert({
      user_id: user.id,
      subject: "Live Chat",
      category: "general",
      is_live: true,
    } as any).select().single();
    if (error) return;
    setTicket(data);
    setMessages([]);
  };

  const handleSend = async () => {
    if (!ticket || !message.trim()) return;
    setSending(true);
    await supabase.from("support_messages").insert({
      ticket_id: ticket.id,
      sender_type: "user",
      sender_id: user.id,
      message: message.trim(),
    } as any);
    await supabase.from("support_tickets").update({
      last_message_at: new Date().toISOString(),
      customer_typing: false,
    } as any).eq("id", ticket.id);
    setMessage("");
    setSending(false);
  };

  const handleTyping = () => {
    if (!ticket) return;
    supabase.from("support_tickets").update({ customer_typing: true } as any).eq("id", ticket.id).then();
    if (typingRef.current) clearTimeout(typingRef.current);
    typingRef.current = setTimeout(() => {
      supabase.from("support_tickets").update({ customer_typing: false } as any).eq("id", ticket.id).then();
    }, 2000);
  };

  // Floating draggable button
  if (!open) {
    return <ChatLauncher unread={unread} onClick={() => { setOpen(true); setMinimized(false); setUnread(0); }} />;

  }

  // Minimized state
  if (minimized) {
    return (
      <div className="fixed bottom-[calc(6rem+env(safe-area-inset-bottom))] right-4 sm:bottom-6 sm:right-6 z-40 w-72 rounded-2xl border border-border bg-card shadow-xl overflow-hidden">
        <div
          onClick={() => { setMinimized(false); setUnread(0); }}
          className="flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground cursor-pointer"
        >
          <div className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4" />
            <span className="text-sm font-medium">Live Chat</span>
            {unread > 0 && <Badge className="h-5 text-[10px] bg-destructive text-destructive-foreground">{unread}</Badge>}
          </div>
          <X className="h-4 w-4 cursor-pointer" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-[calc(6rem+env(safe-area-inset-bottom))] right-4 sm:bottom-6 sm:right-6 z-40 w-[calc(100vw-2rem)] sm:w-[360px] h-[min(520px,calc(100vh-6rem))] sm:h-[520px] rounded-2xl border border-border bg-card shadow-2xl flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4" />
          <span className="text-sm font-medium">Live Chat</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setMinimized(true)} className="hover:opacity-70">
            <Minus className="h-4 w-4" />
          </button>
          <button onClick={() => setOpen(false)} className="hover:opacity-70">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Body */}
      {!ticket ? (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center space-y-4">
            <MessageCircle className="h-12 w-12 mx-auto text-primary/30" />
            <p className="text-sm text-muted-foreground">Start a live conversation with our support team</p>
            <Button onClick={startChat}>Start Chat</Button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-4">Send a message to get started</p>
            )}
            {messages.map((m) => (
              <ChatBubble
                key={m.id}
                message={m.message}
                senderType={m.sender_type}
                timestamp={m.created_at}
                attachments={m.attachments}
              />
            ))}
            {agentTyping && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
                Agent is typing...
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <div className="p-3 border-t border-border">
            <div className="flex gap-2">
              <Textarea
                value={message}
                onChange={(e) => { setMessage(e.target.value); handleTyping(); }}
                placeholder="Type a message..."
                rows={1}
                className="min-h-[36px] max-h-[80px] resize-none text-sm"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
                }}
              />
              <Button size="icon" onClick={handleSend} disabled={sending || !message.trim()} className="h-9 w-9 shrink-0">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
